import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { strings } from "APP_ROOT/localization";
import {
	DispatchHelpers as AssetMonitoringDispatchHelpers,
	Selectors as AssetMonitoringSelectors,
	clearAssetBeaconAssignment,
	setAssetBeaconAssignment,
} from "../../../../../data/assetMonitoring";
import { setSelectedResource } from "../../actions";

import geofencesSelectors from "../../../../../data/geofences/selectors";
import AssetInfoPanel from "./AssetInfoPanel";
import AssetBeaconPanel from "./AssetBeaconPanel";
import ProgressOverlay from "APP_ROOT/components/common/ProgressOverlay";

class AssetDetail extends React.PureComponent {
	state = {
		draftAsset: null,
		errorMessage: null,
	};

    componentDidMount() {
        // If component mounts in edit mode (e.g. creating a new asset), initialize draftAsset
        if (this.props.asset && this.props.asset.editing) {
            this.setState({ draftAsset: { ...this.props.asset } });
        }
    }

	componentDidUpdate(prevProps) {
        const { asset } = this.props;

        if(!asset) return; // no asset selected

		// If we were editing but the editing prop from redux is now false (e.g. saved/cancelled elsewhere), exit edit mode.
		if (this.state.draftAsset && !asset.editing) {
			this.setState({ draftAsset: null });
		}

        if (!this.state.draftAsset && asset.editing) {
			this.setState({ draftAsset: { ...asset } });
		}
	}

	onDeleteAsset = () => {
		const nextAssetToSelect = this.props.nextAssetToSelect;

		this.props
			.deleteAsset()
			.then(() => {
				this.setState({
					errorMessage: null,
				});

                if (!nextAssetToSelect) return; // no more assets to select

                this.props.selectAsset(nextAssetToSelect.assetUuid);
                this.props.pokeMap();
			})
			.catch((error) => {
				console.error(error);
				this.setState({
					errorMessage: strings.resource_action_delete_asset_error_message,
				});
			});
	};

	onEditAsset = (attribute, value) => {
		// beaconUuid is a special case
		// multiple props need to be updated when beaconUuid changes
		if (attribute === "beaconUuid") return this.updateAssetBeaconInfo(value);

		// other props are simple strings
		this.setState({
			draftAsset: {
				...this.state.draftAsset,
				[attribute]: value,
			},
		});
	};

	updateAssetBeaconInfo = (beaconUuid) => {
		const beacon = this.props.availableBeacons.find(
			(beacon) => beacon.beaconUuid === beaconUuid,
		);

		if (!beacon) {
			const clearedAsset = clearAssetBeaconAssignment({
				...this.state.draftAsset,
			});
			this.setState({
				draftAsset: { ...clearedAsset },
			});
			return;
		}

		const assignedAsset = setAssetBeaconAssignment(
			{ ...this.state.draftAsset },
			{ ...beacon },
		);
		this.setState({
			draftAsset: { ...assignedAsset },
		});
	};

	onStartAssetEdit = () => {
		this.props.startAssetEdit(this.props.assetUuid);
	};

	onSaveAssetEdit = () => {
		this.props
			.saveAssetEdit(this.state.draftAsset)
			.then((assetId) => {
				if (this.props.assetUuid !== assetId) {
					this.props.selectAsset(assetId);
				}
				this.setState({
					errorMessage: null,
				});
			})
			.catch((error) => {
				console.error(error);
				this.setState({
					errorMessage: strings.resource_action_save_asset_error_message,
				});
			});
	};

	onCancelAssetEdit = () => {
		this.props.cancelAssetEdit(this.props.assetUuid).then(() => {
			this.setState({
				errorMessage: null,
			});
		});
	};

	renderAssetDetails = () => {
		const assetToDisplay = this.state.draftAsset || this.props.asset;

		return (
			<>
				<AssetInfoPanel
					asset={assetToDisplay}
					onEditAsset={this.onEditAsset}
					stage={this.props.stage}
				/>
				<AssetBeaconPanel
					editable={this.props.editable}
					asset={assetToDisplay}
					availableBeacons={this.props.availableBeacons}
					onEditAsset={this.onEditAsset}
					onStartAssetEdit={this.onStartAssetEdit}
					onSaveAssetEdit={this.onSaveAssetEdit}
					onCancelAssetEdit={this.onCancelAssetEdit}
					onDeleteAsset={this.onDeleteAsset}
					saving={this.props.saving}
					wrapingGeofences={this.props.wrapingGeofences}
					errorMessage={this.state.errorMessage}
				/>
			</>
		);
	};

	renderSpinner = () => {
		return (
			<div className="onboarding-message">{strings.asset_none_selected}</div>
		);
	};

	render() {
		return (
			<div className="card-details-panel asset-details">
				{this.props.asset ? this.renderAssetDetails() : this.renderSpinner()}
				{this.props.saving && <ProgressOverlay />}
				{this.props.assetsLoading && <ProgressOverlay />}
			</div>
		);
	}
}

AssetDetail.propTypes = {
	asset: PropTypes.object,
	assetUuid: PropTypes.string,
	cancelAssetEdit: PropTypes.func,
	deleteAsset: PropTypes.func,
	saveAssetEdit: PropTypes.func,
	saving: PropTypes.bool,
	selectAsset: PropTypes.func.isRequired,
	stage: PropTypes.string,
	startAssetEdit: PropTypes.func,
};

const mapStateToProps = (state, ownProps) => {
	return {
		asset: AssetMonitoringSelectors.asset(state, ownProps.assetUuid), //assets.Selectors.asset(state, ownProps.assetUuid),
		nextAssetToSelect: AssetMonitoringSelectors.nextAssetToSelect(
			state,
			ownProps.assetUuid,
		), // when current asset is deleted, this will contain the next asset to select
		availableBeacons: AssetMonitoringSelectors.availableBeacons(
			state,
			ownProps.assetUuid,
		), // This should contain not-aasigned beacons + beacon for current assret if any
		saving: AssetMonitoringSelectors.assetLoadOrSavePending(
			state,
			ownProps.assetUuid,
		), //!!assets.Selectors.savingAssetEdit(state, ownProps.assetUuid),
		assetsLoading: AssetMonitoringSelectors.loading(state),
		wrapingGeofences: (log, lat) =>
			geofencesSelectors.wrappingGeofences(state, [log, lat]),
	};
};

const mapDispatchToProps = (dispatch, ownProps) => {
	return {
		startAssetEdit: () =>
			dispatch(
				AssetMonitoringDispatchHelpers.startAssetEdit(ownProps.assetUuid),
			), //assets.Actions.startAssetEdit(ownProps.assetUuid)),
		saveAssetEdit: (asset) =>
			dispatch(AssetMonitoringDispatchHelpers.saveAssetEdit(asset)), //assets.Actions.saveAssetEdit(ownProps.stage, asset, ownProps.assetUuid)),
		cancelAssetEdit: () =>
			dispatch(
				AssetMonitoringDispatchHelpers.cancelAssetEdit(ownProps.assetUuid),
			), //assets.Actions.cancelAssetEdit()),
		selectAsset: (assetUuid) =>
			dispatch(setSelectedResource("asset", assetUuid)),
		deleteAsset: () =>
			dispatch(AssetMonitoringDispatchHelpers.deleteAsset(ownProps.assetUuid)),
	};
};

export { AssetDetail as UnconnectedAssetDetail };
export default connect(mapStateToProps, mapDispatchToProps)(AssetDetail);
